import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clear10b51Simulation,
  run10b51Simulation,
} from "../../redux/actions/CompanyAction";
import { Button, Select } from "antd";
import SimulationChart from "./SimulationChart";
import SimulationPercentileTable from "./SimulationPercentileTable";
import PropagateLoader from "react-spinners/PropagateLoader";
import dayjs from "dayjs";

import indexComparisonOptions from "./json/indexComparisonOptions.json";

export default function SimulateStockPrices({
  companyInfo,
  generalInfoParams,
  committedSimulationParams,
  setCommittedSimulationParams,
  setDateFormattedPricePaths,
  RSUCalculatorValues,
}) {
  const dispatch = useDispatch();

  const YEARS =
    generalInfoParams?.[0]?.timeHorizon >= 0
      ? generalInfoParams?.[0]?.timeHorizon
      : 10;
  const ITERATIONS =
    generalInfoParams?.[0]?.iterations >= 0
      ? generalInfoParams?.[0]?.iterations
      : 10;

  const [loading, setLoading] = useState(false);
  const [startSimulation, setStartSimulation] = useState(false);
  const [indexesSelected, setIndexesSelected] = useState([]);

  const { results10b51simulation } = useSelector(
    (state) => state.CompanyReducer,
  );
  // const [pricePaths, setPricePaths] = useState([]);

  useEffect(() => {
    if (results10b51simulation?.data) {
      setLoading(false);
    }
  }, [results10b51simulation]);

  const unadjustedReturns = useMemo(() => {
    return results10b51simulation?.data?.allResults;
  }, [results10b51simulation?.data?.allResults]);

  const truePercentileRuns = useMemo(() => {
    return results10b51simulation?.data?.truePercentileRuns;
  }, [results10b51simulation?.data?.truePercentileRuns]);

  const endPercentileRuns = useMemo(() => {
    return results10b51simulation?.data?.endPercentileRuns;
  }, [results10b51simulation?.data?.endPercentileRuns]);

  const [useTruePercentiles, setUseTruePercentiles] = useState(false); // toggle this later

  const percentiles = useTruePercentiles
    ? truePercentileRuns
    : endPercentileRuns;

  const percentileRuns = useMemo(() => {
    if (!percentiles) return [];

    const percentilesNeeded = [90, 75, 50, 25, 10];
    return percentilesNeeded.map((p) => ({
      percentile: `${p}th`,
      data: percentiles[`p${p}`],
      iterKey: `percentile_${p}`,
      isPercentile: true,
    }));
  }, [percentiles]);

  useMemo(() => {
    if (companyInfo?.Ticker) {
      const companyDrift =
        generalInfoParams?.[0]?.driftAssumption >= 0
          ? generalInfoParams?.[0]?.driftAssumption
          : companyInfo?.Drift || 12;

      // const companyDividendYield =
      //   generalInfoParams?.[0]?.dividendYield >= 0
      //     ? generalInfoParams?.[0]?.dividendYield
      //     : 0;

      if (startSimulation && !loading) {
        setLoading(true);
        const token = localStorage.getItem("ACCESS_TOKEN");
        const ticker = companyInfo.Ticker;
        // To subtract dividend from drift:
        // const drift =
        //   parseFloat(companyDrift) - parseFloat(companyDividendYield);
        const drift = parseFloat(companyDrift);
        const sampleRate = generalInfoParams?.[0]?.sampleRate || "Weekly";

        const searchParams = {
          ticker: ticker,
          n_trials: parseInt(ITERATIONS),
          annual_vol: generalInfoParams[0].volatility / 100,
          starting_price: generalInfoParams[0].sharePrice,
          years: parseInt(YEARS),
          annual_drift: Math.pow(1 + drift / 100, 1 / 52) - 1,
          sample_rate: sampleRate,
          comparison_indexes: indexesSelected,
        };
        dispatch(clear10b51Simulation());
        //Comit to params as it runs, dont re-rednder until next run
        setCommittedSimulationParams({
          generalInfoParams: generalInfoParams,
          YEARS: YEARS,
          ITERATIONS: ITERATIONS,
        });
        dispatch(run10b51Simulation({ searchParams, token }));
        setStartSimulation(false);
      }
    }
  }, [
    dispatch,
    companyInfo,
    generalInfoParams,
    loading,
    ITERATIONS,
    YEARS,
    indexesSelected,
    startSimulation,
    setCommittedSimulationParams,
  ]);

  const [scale, setScale] = useState("log"); // "linear" or "log"

  const indexSelectorOptions = indexComparisonOptions
    .map((item) => ({
      label: item.IndexName,
      value: item.IndexCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleIndexSelectorChange = (value) => {
    setIndexesSelected(value);
  };

  const dateTransformedData = useMemo(() => {
    const START = dayjs(generalInfoParams?.[0]?.currentDate);
    const getIncrement = (totalPoints) => {
      if (totalPoints > 52 * YEARS + 1) return "days";
      if (totalPoints > 12 * YEARS + 1) return "weeks";
      return "months";
    };

    const formatTick = (value, totalPoints, returnFullDate = false) => {
      const unit = getIncrement(totalPoints);
      let date;
      if (unit === "days") date = START.add(parseInt(value) - 1, "day");
      else if (unit === "weeks") date = START.add(parseInt(value) - 1, "week");
      else date = START.add(parseInt(value) - 1, "month");

      return returnFullDate ? date.format("M/D/YYYY") : date.year();
    };

    if (!unadjustedReturns || !unadjustedReturns?.length) return;

    return unadjustedReturns.map((trial) =>
      trial.map((value, index) => ({
        date: formatTick(index, trial.length, true),
        value: value,
      })),
    );
  }, [unadjustedReturns, YEARS, generalInfoParams]);

  const dividendPaymentSchedule = useMemo(() => {
    if (
      !dateTransformedData ||
      !dateTransformedData.length ||
      !dateTransformedData?.[0]?.length
    )
      return;

    const getDividendPaymentDates = (
      nextDividendDate,
      vestDate,
      frequencyMonths,
    ) => {
      const dates = [];
      const end = dayjs(vestDate);

      for (
        let date = dayjs(nextDividendDate);
        date.isBefore(end) || date.isSame(end);
        date = date.add(frequencyMonths, "month")
      ) {
        dates.push(date);
      }

      return dates;
    };

    // Step 1: build full dividend schedule outside the forEach
    const nextDividendDate = generalInfoParams?.[0]?.dividendDate;
    // dividendFrequencyMonths: 1 for month, 3 for quarter, 12 for annual:
    const dividendFrequencyMonths =
      generalInfoParams?.[0]?.dividendFrequency === "Monthly" ? 1 : 3;
    const exactDividendAmount = generalInfoParams?.[0]?.dividendAmount;
    const dividendGrowthRate = generalInfoParams?.[0]?.dividendGrowthRate / 100;

    const lastVestDate = dayjs(
      Math.max(
        ...dateTransformedData[0].map((item) => dayjs(item.date).valueOf()),
      ),
    );

    const allPaymentDates = getDividendPaymentDates(
      nextDividendDate,
      lastVestDate,
      dividendFrequencyMonths,
    );
    const today = dayjs();

    const dividendSchedule = allPaymentDates.map((date, index) => {
      const yearsFromNow = date.diff(today, "day") / 365;
      const projectedDividend =
        exactDividendAmount * Math.pow(1 + dividendGrowthRate, yearsFromNow);

      const projectedDividendPV = !isNaN(RSUCalculatorValues?.[0]?.riskFreeRate)
        ? parseFloat(
            projectedDividend *
              Math.exp(
                -(RSUCalculatorValues?.[0]?.riskFreeRate / 100) * yearsFromNow,
              ),
          )
        : projectedDividend;

      return {
        key: index,
        date: date.format("M/D/YYYY"),
        originalDividend: exactDividendAmount,
        projectedDividend,
        projectedDividendPV,
      };
    });
    return dividendSchedule;
  }, [generalInfoParams, dateTransformedData, RSUCalculatorValues]);

  const dividendAdjustedData = useMemo(() => {
    const startingPriceString = generalInfoParams?.[0]?.sharePrice;
    if (!dateTransformedData || !startingPriceString) return;
    const startingPrice = parseFloat(startingPriceString);
    return dateTransformedData.map((trial, trialNumber) => {
      const dividendPaymentSchedulePayouts = [
        ...dividendPaymentSchedule.map((item) => ({
          ...item,
          alreadyPaid: false,
        })),
      ];

      let price = startingPrice;
      let previousPrice = startingPrice;

      const dividendAdjustedTrial = trial.map((item, index) => {
        const date = item.date;
        const periodReturn = item.value; //e.g. monthlyReturn/weeklyReturn

        const nextDividendIndex = dividendPaymentSchedulePayouts.findIndex(
          (payment) =>
            (dayjs(payment.date).isSame(date) ||
              dayjs(payment.date).isBefore(date)) &&
            !payment?.alreadyPaid,
        );

        const currentPrice = previousPrice * (1 + periodReturn);

        let dividendAdjustedPrice = null;
        if (nextDividendIndex > -1) {
          const currentDividend =
            dividendPaymentSchedulePayouts[nextDividendIndex];

          const dividendAdjustedShock = Math.max(
            Math.max(
              currentPrice -
                currentDividend.projectedDividendPV -
                previousPrice,
              0,
            ) / previousPrice,
            -1,
          );

          dividendAdjustedPrice = Math.max(
            previousPrice * (1 + dividendAdjustedShock),
            0,
          );

          if (trialNumber === 1) {
            console.log(
              `date: ${date}\n`,
              `divDate: ${dividendPaymentSchedulePayouts[nextDividendIndex].date}\n`,
              `previousPrice: ${previousPrice}\n`,
              `1+return: ${1 + periodReturn}\n`,
              `newPrice (unadjusted): ${currentPrice}\n`,
              `currentDividend: ${currentDividend.projectedDividendPV}\n`,
              `dividendAdjusted 1+return: ${1 + dividendAdjustedShock}\n`,
              `dividendAdjustedPrice: ${dividendAdjustedPrice}\n`,
            );
          }
          dividendPaymentSchedulePayouts[nextDividendIndex]["alreadyPaid"] =
            true;
        }

        price = dividendAdjustedPrice || currentPrice;
        previousPrice = price;

        return {
          date: dayjs(date).format("M/D/YYYY"),
          value: parseFloat(price),
        };
      });
      dividendAdjustedTrial.unshift({
        date: dayjs(generalInfoParams?.[0]?.currentDate).format("M/D/YYYY"),
        value: parseFloat(generalInfoParams?.[0]?.sharePrice),
      });
      return dividendAdjustedTrial;
    });
  }, [dateTransformedData, dividendPaymentSchedule, generalInfoParams]);

  const dividendAdjustedPricePaths = useMemo(() => {
    if (
      !dividendAdjustedData ||
      !dividendAdjustedData?.length ||
      !dividendAdjustedData?.[0]?.length ||
      !generalInfoParams?.[0]?.sharePrice ||
      isNaN(parseFloat(generalInfoParams?.[0]?.sharePrice))
    )
      return;
    return dividendAdjustedData.map((trial) => {
      const trialData = trial.map((item) => item.value);
      return trialData;
    });
  }, [dividendAdjustedData, generalInfoParams]);

  useEffect(() => {
    setDateFormattedPricePaths(dividendAdjustedData);
  }, [dividendAdjustedData, setDateFormattedPricePaths]);

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexWrap: "nowrap",
          marginTop: 20,
        }}
        className="hasHoverText"
      >
        <h3
          style={{
            fontSize: 20,
            marginRight: 5,
            marginBottom: 5,
            marginTop: 5,
          }}
        >
          {"Simulate with Indexes:".split(" ").map((head, index) => (
            <span key={index}>{head}&nbsp;</span>
          ))}
        </h3>
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          placeholder={`Select Indexes to simulate with ${companyInfo?.Company ? companyInfo?.Company : "target company"}`}
          onChange={handleIndexSelectorChange}
          options={indexSelectorOptions}
        />
      </div>
      <div style={{ margin: 20 }} />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button onClick={() => setStartSimulation(true)} disabled={loading}>
          Start Simulation
        </Button>
      </div>
      <div style={{ margin: 20 }} />

      {(loading ||
        (dividendAdjustedPricePaths &&
          dividendAdjustedPricePaths.length > 0)) && (
        <h2>Simulation Results</h2>
      )}

      {loading ? (
        <center
          style={{
            minHeight: "75px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PropagateLoader
            color={"var(--loader)"}
            loading={loading}
            size={17.5}
          />
        </center>
      ) : dividendAdjustedPricePaths &&
        dividendAdjustedPricePaths.length > 0 ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {dividendAdjustedPricePaths &&
              dividendAdjustedPricePaths.length > 0 && (
                <SimulationChart
                  data={dividendAdjustedPricePaths}
                  scale={scale}
                  generalInfoParams={
                    committedSimulationParams?.generalInfoParams
                  }
                  YEARS={committedSimulationParams?.YEARS}
                  ITERATIONS={committedSimulationParams?.ITERATIONS}
                  percentileRuns={percentileRuns}
                />
              )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginLeft: "2%",
            }}
          >
            <Button
              onClick={() =>
                setScale((s) => (s === "linear" ? "log" : "linear"))
              }
            >
              Switch to {scale === "linear" ? "Logarithmic" : "Linear"} View
            </Button>
            <Button
              style={{ marginLeft: 20 }}
              onClick={() => setUseTruePercentiles((s) => !s)}
            >
              Switch to {!useTruePercentiles ? "Multi-Point" : "End-Point"}{" "}
              Percentiles
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginLeft: "-0.5%",
              marginTop: "1%",
            }}
          >
            <Button
              onClick={() => {
                dispatch(clear10b51Simulation());
              }}
              disabled={loading}
            >
              Clear Simulation
            </Button>
          </div>

          <h2>Summary Stats Table</h2>
          <div
            style={{
              justifyContent: "center",
              marginTop: 20,
              width: "100%",
            }}
          >
            {truePercentileRuns && (
              <SimulationPercentileTable
                truePercentileRuns={truePercentileRuns}
                YEARS={committedSimulationParams?.YEARS}
              />
            )}
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
}
